import { useState } from "react";
import { Mail, Phone, Trash2, Calendar, MapPin, Download } from "lucide-react";
import SendToPartner from "./SendToPartner";
import { apiClient } from "../lib/api";
import { toast } from "sonner";

const STATUSES = [
  { v: "nouveau", l: "Nouveau", cls: "bg-accent/10 text-accent" },
  { v: "transmis", l: "Transmis", cls: "bg-blue-100 text-blue-700" },
  { v: "signe", l: "Signé", cls: "bg-emerald-100 text-emerald-700" },
  { v: "perdu", l: "Perdu", cls: "bg-slate-100 text-slate-600" },
];
const LABELS = {
  appartement: "Appartement", maison: "Maison", villa: "Villa", studio: "Studio", autre: "Autre",
  deja_loue: "Déjà loué", pas_encore: "Pas encore loué", projet_achat: "Projet d'achat",
  toute_annee: "Toute l'année", saisonnier: "Saisonnier", occasionnel: "Occasionnel",
  asap: "Dès que possible", "1_3_mois": "1 à 3 mois", plus_tard: "Plus tard",
};

const CSV_COLS = [["created_at", "Date"], ["first_name", "Prénom"], ["last_name", "Nom"], ["email", "Email"], ["phone", "Téléphone"], ["city_name", "Commune"],
  ["property_type", "Type"], ["bedrooms", "Chambres"], ["capacity", "Voyageurs"], ["rental_status", "Situation"], ["rental_mode", "Location"],
  ["services", "Services"], ["start_timing", "Démarrage"], ["simulator_low", "Estimation min"], ["simulator_high", "Estimation max"], ["message", "Message"],
  ["status", "Statut"], ["assigned_conciergerie", "Conciergerie"], ["notes", "Notes"]];

function exportCsv(quotes) {
  const esc = (v) => `"${String(Array.isArray(v) ? v.join(", ") : v ?? "").replace(/"/g, '""')}"`;
  const lines = [CSV_COLS.map((c) => c[1]).join(";"), ...quotes.map((q) => CSV_COLS.map(([k]) => esc(q[k])).join(";"))];
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

export default function AdminQuotesList({ quotes, setQuotes, onChange, partners = [] }) {
  const [filter, setFilter] = useState("all");
  const shown = filter === "all" ? quotes : quotes.filter((q) => q.status === filter);

  const patch = async (id, body) => {
    await apiClient.patch(`/admin/devis/${id}`, body);
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, ...body } : q)));
    onChange?.();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette demande de devis ?")) return;
    await apiClient.delete(`/admin/devis/${id}`);
    setQuotes((qs) => qs.filter((q) => q.id !== id));
    toast.success("Demande supprimée");
    onChange?.();
  };

  return (
    <div data-testid="quotes-list">
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <button onClick={() => exportCsv(shown)} className="btn-primary py-2 px-4 text-xs mr-2" data-testid="quotes-export-csv"><Download size={13} /> Exporter en CSV</button>
        {[{ v: "all", l: "Toutes" }, ...STATUSES].map((s) => (
          <button key={s.v} onClick={() => setFilter(s.v)} data-testid={`quotes-filter-${s.v}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${filter === s.v ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
            {s.l} ({s.v === "all" ? quotes.length : quotes.filter((q) => q.status === s.v).length})
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">Aucune demande de devis</div>
      ) : (
        <div className="space-y-3">
          {shown.map((q) => (
            <QuoteRow key={q.id} q={q} patch={patch} remove={remove} partners={partners}
              onSent={(entry, name) => { setQuotes((qs) => qs.map((x) => (x.id === q.id ? { ...x, status: "transmis", assigned_conciergerie: name, sent_to: [...(x.sent_to || []), entry] } : x))); onChange?.(); }} />
          ))}
        </div>
      )}
    </div>
  );
}

function QuoteRow({ q, patch, remove, partners, onSent }) {
  const [assigned, setAssigned] = useState(q.assigned_conciergerie || "");
  const saveAssigned = () => {
    if (assigned === (q.assigned_conciergerie || "")) return;
    patch(q.id, { assigned_conciergerie: assigned }).then(() => toast.success("Conciergerie enregistrée"));
  };
  const st = STATUSES.find((s) => s.v === q.status) || STATUSES[0];

  return (
    <div className="surface-card p-5 grid md:grid-cols-12 gap-4 items-start" data-testid={`quote-row-${q.id}`}>
      <div className="md:col-span-3">
        <div className="font-semibold">{q.first_name} {q.last_name}</div>
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar size={11} /> {new Date(q.created_at).toLocaleString("fr-FR")}</div>
        <div className="text-sm mt-2 space-y-1">
          <div className="flex items-center gap-1.5"><Mail size={12} className="text-accent" /> {q.email}</div>
          <div className="flex items-center gap-1.5"><Phone size={12} className="text-accent" /> {q.phone}</div>
        </div>
      </div>
      <div className="md:col-span-4 text-sm space-y-1">
        <div className="font-semibold flex items-center gap-1.5"><MapPin size={12} className="text-accent" /> {q.city_name}</div>
        <div className="text-muted-foreground">{LABELS[q.property_type]} · {q.bedrooms} ch · {q.capacity} pers.</div>
        <div className="text-muted-foreground">{LABELS[q.rental_status]} · {LABELS[q.rental_mode]} · {LABELS[q.start_timing]}</div>
        <div className="text-xs text-muted-foreground">{q.services?.join(", ")}</div>
        {q.simulator_low && <div className="text-xs font-semibold text-accent">Simulateur : {q.simulator_low.toLocaleString("fr-FR")} – {q.simulator_high.toLocaleString("fr-FR")} €</div>}
        {q.message && <p className="text-xs italic text-foreground/80 mt-1">« {q.message} »</p>}
      </div>
      <div className="md:col-span-4 space-y-2">
        <select value={q.status} onChange={(e) => patch(q.id, { status: e.target.value })} data-testid={`quote-status-select-${q.id}`}
          className={`w-full text-sm font-semibold rounded-full px-4 py-2 border-0 ${st.cls}`}>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <input value={assigned} onChange={(e) => setAssigned(e.target.value)} onBlur={saveAssigned} placeholder="Conciergerie à qui transmettre…"
          className="w-full text-sm rounded-full px-4 py-2 border border-input bg-background" data-testid={`quote-assigned-${q.id}`} />
        <SendToPartner quote={q} partners={partners} onSent={onSent} />
        {q.sent_to?.length > 0 && (
          <div className="text-xs text-muted-foreground space-y-0.5" data-testid={`quote-sent-history-${q.id}`}>
            {q.sent_to.map((s) => <div key={s.at}>Envoyé à {s.name} ({s.to}) le {new Date(s.at).toLocaleString("fr-FR")}</div>)}
          </div>
        )}
      </div>
      <div className="md:col-span-1 text-right">
        <button onClick={() => remove(q.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-quote-${q.id}`}><Trash2 size={14} /></button>
      </div>
    </div>
  );
}
